import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { chain, map, sumBy } from 'lodash';
import { Col, Row } from 'reactstrap';
import { TableBody, TableCell, TableHead, TableRow, Container, Grid } from '@mui/material';
import { LineItem } from '@payer-admin-ui/api/src/orders/models/LineItem';
import { ItemType } from '@payer-admin-ui/api/src/invoices/models/ItemType';
import { CurrencyCode } from '@payer-admin-ui/api/src/invoices/models/CurrencyCode';
import { BasicButton } from '@payer-admin-ui/components-v2/src/buttons/BasicButton';
import { IconType } from '@payer-admin-ui/components-v2/src/icons/BasicIcon';
import { ExpandableContainer, Table } from '../../..';
import { isTruthy } from '../../utils/extensions/typescriptExtensions';
import { LineItemEntry } from './LineItemEntry';
import { SummaryLine, VatSummaryLine } from './SummaryLines';
import { EditItemRowModal } from './EditItemRowModal';
import { EditInfoRowModal } from './EditInfoRowModal';

const calculateSummary = (items: LineItem[]) => {
  const freeformItems = items.filter(item => item.itemType === ItemType.FREEFORM);

  const sumTotalExcludingVat = sumBy(freeformItems, item => {
    return item.subtotalPrice - item.subtotalVatAmount;
  });
  const totalAmountIncludingVat = sumBy(freeformItems, 'subtotalPrice');

  const sumTotalVatAmount = sumBy(freeformItems, 'subtotalVatAmount');

  const vatSums = chain(freeformItems)
    .groupBy('vatPercentage')
    .mapValues((items, vatPercentage) => ({
      vatPercentage,
      vatBasis: sumBy(items, 'subtotalPrice') - sumBy(items, 'subtotalVatAmount'),
      vatAmount: sumBy(items, 'subtotalVatAmount'),
    }))
    .values()
    .value();

  return {
    vatSums: vatSums,
    sumTotalVatAmount: sumTotalVatAmount,
    sumTotalExcludingVat: sumTotalExcludingVat,
    sumAmountToPay: totalAmountIncludingVat,
  };
};

export type ItemsAndSummaryProps = {
  items: LineItem[];
  currencyCode: CurrencyCode;
  setItems?: (lineItems: LineItem[]) => void;
  disabled: boolean;
  expanded?: boolean;
};

const ItemsAndSummary = ({ items, currencyCode, setItems, disabled, expanded }: ItemsAndSummaryProps) => {
  const summary = calculateSummary(items);
  const { t } = useTranslation();
  const [itemModelOpen, setItemModelOpen] = useState(false);
  const [infoModelOpen, setInfoModelOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(0);

  const openItemModal = () => {
    setSelectedIndex(items.length);
    setItemModelOpen(true);
  };
  const openInfoModal = () => {
    setSelectedIndex(items.length);
    setInfoModelOpen(true);
  };

  const closeModal = () => {
    setItemModelOpen(false);
    setInfoModelOpen(false);
    setSelectedIndex(undefined);
  };

  const addRow = (lineItem: Omit<LineItem, 'position'>, index: number) => {
    if (setItems) {
      setInfoModelOpen(false);
      setItemModelOpen(false);
      setItems([...items.slice(0, index), { ...lineItem, position: index + 1 }, ...items.slice(index + 1)]);
    }
  };

  const deleteRow = (indexToRemove: number) => {
    if (setItems) {
      setItems(
        items
          .map((item, index) =>
            index === indexToRemove ? undefined : { ...item, position: index + (index > indexToRemove ? 0 : 1) },
          )
          .filter(isTruthy),
      );
    }
  };

  const editRow = (index: number) => {
    if (index < items.length) {
      setSelectedIndex(index);
      if (items[index].itemType === ItemType.FREEFORM) {
        setItemModelOpen(true);
      } else if (items[index].itemType === ItemType.INFOLINE) {
        setInfoModelOpen(true);
      }
    }
  };

  return (
    <ExpandableContainer header={<b>{t('components.cards.invoice.items')}</b>} defaultExpanded={expanded ?? true}>
      <div className="my-4">
        <Container maxWidth={false} disableGutters className="mb-4">
          <Grid container flexDirection="column" alignItems="flex-end" spacing={1}>
            <Grid item className="w-100">
              <Table striped>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t('invoices.fields.lineItem.fields.articleNumber')}</TableCell>
                    <TableCell>{t('invoices.fields.lineItem.fields.description')}</TableCell>
                    <TableCell>{t('invoices.fields.lineItem.fields.quantity')}</TableCell>
                    <TableCell>{t('invoices.fields.lineItem.fields.unitPrice')}</TableCell>
                    <TableCell>{t('invoices.fields.lineItem.fields.vatPercentage')}</TableCell>
                    <TableCell>{t('invoices.fields.lineItem.fields.subtotalPrice')}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {map(items, (item, index) => {
                    return (
                      <LineItemEntry
                        key={item.position}
                        item={item}
                        index={index}
                        editRowCallback={editRow}
                        deleteRowCallback={deleteRow}
                        disabled={disabled}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </Grid>
            <Grid item>
              {!disabled && (
                <>
                  <BasicButton
                    variant="contained"
                    startIcon={IconType.PLUS}
                    color="primary"
                    sx={{ mr: 2 }}
                    onClick={openItemModal}
                  >
                    {t('invoices.CreateInvoicePage.addItemRow')}
                  </BasicButton>
                  <BasicButton variant="contained" startIcon={IconType.PLUS} color="primary" onClick={openInfoModal}>
                    {t('invoices.CreateInvoicePage.addInfoRow')}
                  </BasicButton>
                  {itemModelOpen && typeof selectedIndex !== 'undefined' && (
                    <EditItemRowModal
                      item={selectedIndex < items.length ? items[selectedIndex] : undefined}
                      currencyCode={currencyCode}
                      onAddOrUpdateItemRow={lineItem => addRow(lineItem, selectedIndex)}
                      onClose={closeModal}
                    />
                  )}
                  {infoModelOpen && typeof selectedIndex !== 'undefined' && (
                    <EditInfoRowModal
                      infoLine={selectedIndex < items.length ? items[selectedIndex].description : undefined}
                      onAddOrUpdateInfoRow={lineItem => addRow(lineItem, selectedIndex)}
                      onClose={closeModal}
                    />
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Container>
        <Row className="mt-3">
          <Col xs={{ offset: 4, size: 8 }}>
            <Table striped>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>{t('components.cards.invoice.summary.sumSymbol')}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t('components.cards.invoice.summary.vatPercentage')}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t('components.cards.invoice.summary.vatBasis')}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t('components.cards.invoice.summary.vatAmount')}</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.vatSums.map(vatSum => (
                  <VatSummaryLine
                    key={vatSum.vatPercentage}
                    vatPercentage={vatSum.vatPercentage}
                    vatBasis={vatSum.vatBasis}
                    vatAmount={vatSum.vatAmount}
                  />
                ))}
              </TableBody>
            </Table>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={{ offset: 4, size: 8 }}>
            <Table striped>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <b>{t('components.cards.invoice.summary.sumSymbol')}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t('components.cards.invoice.summary.totalVatAmount')}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t('components.cards.invoice.summary.totalExcludingVat')}</b>
                  </TableCell>
                  <TableCell>
                    <b>{t('components.cards.invoice.summary.amountToPay')}</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <SummaryLine
                  sumTotalVatAmount={summary.sumTotalVatAmount}
                  sumTotalExcludingVat={summary.sumTotalExcludingVat}
                  sumAmountToPay={summary.sumAmountToPay}
                  currencyCode={currencyCode}
                />
              </TableBody>
            </Table>
          </Col>
        </Row>
      </div>
    </ExpandableContainer>
  );
};

export { ItemsAndSummary };
